import { useTranslation } from "react-i18next";
import {
  FormContainer,
  ISelectType,
  Loading,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { memo, useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import {
  unchosenProfessionalPrioritySchema,
  TUnchosenProfessionalPriorityForm,
} from "./unchosenProfessionalPrioritySchema";
import {
  createTicketAutRecRuleUnchosenProfessionalRule,
  getTicketAutRecRuleUnchosenProfessionalRuleById,
  updateTicketAutRecRuleUnchosenProfessionalRuleById,
} from "../../../../../../../services/ticketAutRecRuleUnchosenProfessionalRule.service";
import { IUnchosenProfessionalPriorityProps } from "./models";
import { listGenerics } from "../../../../../../../services/generic.service";
import { UnchosenProfessionalFields } from "./UnchosenProfessionalFields";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutRecRuleUnchosenProfessionalRule.";

export const UnchosenProfessionalPriority = memo(
  ({
    id,
    ticketAutomationReceivedRuleId,
    onBackButton,
  }: IUnchosenProfessionalPriorityProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createTicketAutRecRuleUnchosenProfessionalRule,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateTicketAutRecRuleUnchosenProfessionalRuleById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getTicketAutRecRuleUnchosenProfessionalRuleById,
    );

    const {
      sendRequest: getListGenericsRequest,
      loading: loadingListGenerics,
    } = useFetch(listGenerics);

    const [unchosenProfessionalCriterions, setUnchosenProfessionalCriterions] =
      useState<ISelectType[]>();

    const updateHandler = (v: TUnchosenProfessionalPriorityForm, id: string) =>
      update({
        item: {
          id: id,
          ...v,
          ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
          initialAge: Number(v.initialAge),
          finalAge: Number(v.finalAge),
          priority: Number(v.priority),
          unchosenProfessionalCriterionId: v.unchosenProfessionalCriterion.id,
          insuranceRequests: v.insuranceRequests?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
          offerRequests:
            v.thenPriorities?.map((item) => ({
              priority: Number(item.offerPriority) || undefined,
              calendarExternalId: item.calendars?.id,
              calendarExternalName: item.calendars?.name,
              doctorExternalId: item.physicians?.id,
              doctorExternalName: item.physicians?.name,
            })) ?? undefined,
          specialtyRequests: v.specialtyRequests?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        },
        id: id,
      });

    const saveHandler = (v: TUnchosenProfessionalPriorityForm) =>
      save({
        ...v,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
        initialAge: Number(v.initialAge),
        finalAge: Number(v.finalAge),
        priority: Number(v.priority),
        unchosenProfessionalCriterionId: v.unchosenProfessionalCriterion.id,
        insuranceRequests: v.insuranceRequests?.map((item) => ({
          externalId: item.id,
          externalName: item.name,
        })),
        offerRequests:
          v.thenPriorities?.map((item) => ({
            priority: Number(item.offerPriority) || undefined,
            calendarExternalId: item.calendars?.id,
            calendarExternalName: item.calendars?.name,
            doctorExternalId: item.physicians?.id,
            doctorExternalName: item.physicians?.name,
          })) ?? undefined,
        specialtyRequests: v.specialtyRequests?.map((item) => ({
          externalId: item.id,
          externalName: item.name,
        })),
      });

    const getHandler = useCallback(
      async (
        id: string,
      ): Promise<Treturn<TUnchosenProfessionalPriorityForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TUnchosenProfessionalPriorityForm = {
            ...data,
            initialAge: data.initialAge ? String(data.initialAge) : null,
            finalAge: data.finalAge ? String(data.finalAge) : null,
            priority: data.priority ? String(data.priority) : null,
            thenPriorities:
              data.ticketAutRecRuleUnchosenProfessionalRuleOffers?.map(
                (item) => ({
                  offerPriority: item.priority
                    ? String(item.priority)
                    : undefined,
                  ...(item.calendarExternalId && {
                    calendars: {
                      id: item.calendarExternalId,
                      name: item.calendarExternalName,
                      code: item.calendarExternalId,
                    },
                  }),
                  ...(item.doctorExternalId && {
                    physicians: {
                      id: item.doctorExternalId,
                      name: item.doctorExternalName,
                      code: item.doctorExternalId,
                    },
                  }),
                }),
              ),
            insuranceRequests:
              data.ticketAutRecRuleUnchosenProfessionalRuleInsurances?.map(
                (item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }),
              ),
            specialtyRequests:
              data.ticketAutRecRuleUnchosenProfessionalRuleSpecialties?.map(
                (item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }),
              ),
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get],
    );

    const fetchListGenerics = useCallback(async () => {
      const { success, data } = await getListGenericsRequest({
        Identifier: "UnchosenProfessionalCriterion",
      });

      if (success && data) {
        setUnchosenProfessionalCriterions(data);
      }
    }, [getListGenericsRequest]);

    useEffect(() => {
      fetchListGenerics();
    }, [fetchListGenerics]);

    const loading = sLoading || uLoading || gLoading;

    if (loadingListGenerics) {
      return <Loading />;
    }

    return (
      <FormContainer<TUnchosenProfessionalPriorityForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "unchosenProfessionalPriority")}
        schema={unchosenProfessionalPrioritySchema}
        subtitle={t(translationPath + "new_unchosenProfessionalPriority")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        defaultValues={{
          unchosenProfessionalCriterion: unchosenProfessionalCriterions?.find(
            (item) => item.code === "2",
          ),
          thenPriorities: [{}],
        }}
        fields={() => (
          <Grid container spacing={2}>
            <UnchosenProfessionalFields
              unchosenProfessionalCriterions={unchosenProfessionalCriterions}
            />
          </Grid>
        )}
      />
    );
  },
);
